import * as Sentry from '@sentry/react';
import { Text } from '@wirechunk/ui';
import type { FunctionComponent, PropsWithChildren } from 'react';
import { environment } from '../env.ts';

export { captureException } from '@sentry/react';

const { admin } = window.wirechunk;

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  // Note: We used to capture errors logged to the console so that errors can be recorded from the ui package.
  // But we don't do that anymore because nothing in the ui package should directly
  integrations: [
    Sentry.browserTracingIntegration({
      // Sentry doesn't do a good job of capturing and nesting spans. Only the first few fetch requests are
      // captured, and they're placed under the "pageload" transaction.
      traceFetch: false,
    }),
    ...(admin
      ? [
          Sentry.replayIntegration({
            maskAllText: false,
            blockAllMedia: false,
          }),
        ]
      : []),
  ],
  environment,
  release: import.meta.env.VITE_APP_VERSION,
  tracesSampleRate: 1,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1,
});

const fallback = (
  <div className="m-2 p-3 border-1 border-round">
    <h1 className="text-lg mt-1 mb-3">Error</h1>
    <Text className="block" weight="medium">
      Sorry, some unhandled error occurred. We got notified and will look into it.
    </Text>
  </div>
);

type ErrorBoundaryProps = PropsWithChildren<{
  showDialog?: boolean;
}>;

export const ErrorBoundary: FunctionComponent<ErrorBoundaryProps> = ({ showDialog, children }) => (
  <Sentry.ErrorBoundary fallback={fallback} showDialog={showDialog}>
    {children}
  </Sentry.ErrorBoundary>
);
